import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

type ReportRow = {
  fleetId: string;
  fleetName: string;
  units: number;
  unitsActive: number;
  unitsOutOfService: number;
  pendingAlerts: number;
  overdueAlerts: number;
  openAppointments: number;
  totalAmount: number;
  invoicesTotal: number;
  budgetsTotal: number;
};

const fmtMoney = (value: number) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(value || 0);

const FleetReports: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ReportRow[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/fleets/reports/summary');
      setRows((response.data?.report || []) as ReportRow[]);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudieron cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.fleets += 1;
        acc.units += Number(r.units || 0);
        acc.alerts += Number(r.pendingAlerts || 0);
        acc.overdue += Number(r.overdueAlerts || 0);
        acc.totalAmount += Number(r.totalAmount || 0);
        return acc;
      },
      { fleets: 0, units: 0, alerts: 0, overdue: 0, totalAmount: 0 }
    );
  }, [rows]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reportes de flotas</h1>
          <p className="text-sm text-muted-foreground">KPIs operativos y comerciales por empresa</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/fleets')}>Volver</Button>
      </div>

      <div className="grid gap-3 md:grid-cols-5">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Flotas</div><div className="text-2xl font-bold">{totals.fleets}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unidades</div><div className="text-2xl font-bold">{totals.units}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Alertas</div><div className="text-2xl font-bold">{totals.alerts}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Vencidas</div><div className="text-2xl font-bold text-red-600">{totals.overdue}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total estimado</div><div className="text-xl font-bold">{fmtMoney(totals.totalAmount)}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Detalle por flota</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Flota</TableHead>
                  <TableHead>Unidades</TableHead>
                  <TableHead>Operativas</TableHead>
                  <TableHead>Alertas</TableHead>
                  <TableHead>Vencidas</TableHead>
                  <TableHead>Turnos abiertos</TableHead>
                  <TableHead>Facturado</TableHead>
                  <TableHead>Presupuestado</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={10}>Cargando...</TableCell></TableRow>}
                {!loading && rows.length === 0 && <TableRow><TableCell colSpan={10}>Sin datos</TableCell></TableRow>}
                {!loading && rows.map((r) => (
                  <TableRow key={r.fleetId}>
                    <TableCell>{r.fleetName}</TableCell>
                    <TableCell>{r.units}</TableCell>
                    <TableCell>{r.unitsActive}</TableCell>
                    <TableCell>{r.pendingAlerts}</TableCell>
                    <TableCell>{r.overdueAlerts}</TableCell>
                    <TableCell>{r.openAppointments}</TableCell>
                    <TableCell>{fmtMoney(r.invoicesTotal || 0)}</TableCell>
                    <TableCell>{fmtMoney(r.budgetsTotal || 0)}</TableCell>
                    <TableCell>{fmtMoney(r.totalAmount || 0)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => navigate(`/fleets/${r.fleetId}`)}>Ver</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FleetReports;
